import type { PartDefinition, MaterialInfo, MaterialValue, Adjustment } from '@rogue/rackbuilder'
import { AdjustOnlyVisual } from '../../../constants'
import { getRackModel, MONSTER_SPOTTER_ARMS } from '../../../modelPaths'
import { RackOptions } from '../../../RackTypes'
import { MaterialBuilder } from '../builders/materialBuilder'
import { AccessoryBuilder } from '../builders/accessoryBuilder'
import { addPlateStorage } from '../../../utils/plate-storage'

/**
 * Builds RML Half Rack and RM Collegiate Half Rack
 * Handles both single and double collegiate racks
 */
export class HalfRackBuilder {
  protected line: RackOptions['line']
  protected material: MaterialInfo
  protected materialBuilder: MaterialBuilder
  protected accessoryBuilder: AccessoryBuilder
  protected sideTransform: Partial<PartDefinition>
  
  protected uprightLine: string
  protected crossmemberLine: string
  protected pullupLine: string
  protected jcupLine: string
  protected storageLine: string

  constructor(protected config: RackOptions) {
    this.line = config.line
    this.materialBuilder = new MaterialBuilder(config)
    this.material = this.materialBuilder.createMainMaterial()
    this.accessoryBuilder = new AccessoryBuilder(config, this.material)
    this.sideTransform = {}
    
    this.uprightLine = `${this.line}_UPRIGHTS`
    this.crossmemberLine = `${this.line}_CROSSMEMBERS`
    this.pullupLine = `${this.line}_PULLUP`
    this.jcupLine = `${this.line}_JCUPS`
    this.storageLine = `${this.line}_STORAGE`
  }

  public build(): PartDefinition {
    return this.config.doubleRack 
      ? this.assembleDoubleRack()
      : this.assembleSingleRack()
  }

  protected assembleSingleRack(): PartDefinition {
    const leftSide = this.createSide(true)
    const rightSide = this.createSide(false)

    leftSide.slots['other-side'] = rightSide
    leftSide.id = 'main'

    return leftSide
  }

  protected assembleDoubleRack(): PartDefinition {
    const leftSide = this.createSide(true, true)
    const rightSide = this.createSide(false, true)

    leftSide.slots['other-side'] = rightSide
    leftSide.id = 'main'

    return leftSide
  }

  // Accessories, branding, and bench all attach to the left side
  protected createSide(left: boolean, isDouble = false): PartDefinition {
    this.sideTransform = this.getSideTransform(left)
    
    const frontUpright = this.createFrontUpright()
    const backUpright = this.createBackUpright()
    
    // Connect structure
    this.connectStructure(frontUpright, backUpright, left)
    
    // Add accessories
    this.attachJCup(this.config.isCollegiateRack ? frontUpright : backUpright, left)
    this.attachSpotterArms(frontUpright, left)
    this.attachPullupBar(frontUpright, left)
    this.attachNameplate(backUpright, left)
    this.attachPlateStorage(backUpright, left)
    
    // Add double rack components if needed
    if (isDouble) {
      this.addDoubleRackComponents(backUpright, left)
    }
    
    return frontUpright
  }

  protected getSideTransform(left: boolean): Partial<PartDefinition> {
    return left
      ? {}
      : {
        adjustments    : [{ type: 'rotate', axis: 'Y', amount: 180 }],
        adjustmentType : AdjustOnlyVisual,
      }
  }

  protected createFrontUpright(): PartDefinition {
    return {
      mesh      : getRackModel(this.uprightLine, this.config.isCollegiateRack ? 'upright numbered 100' : 'upright numbered 90'),
      slots     : {},
      materials : this.material,
      ...this.sideTransform,
    }
  }

  protected createBackUpright(): PartDefinition {
    return {
      mesh      : getRackModel(this.uprightLine, 'upright numbered 90'),
      slots     : {},
      anchor    : 'crossmember-bottom',
      materials : this.material,
      ...this.sideTransform,
    }
  }

  //  Connects the front and back uprights with crossmembers and stabilizer
  protected connectStructure(frontUpright: PartDefinition, backUpright: PartDefinition, left: boolean): void {
    // Bottom crossmember
    const bottomCrossmember = this.createBottomCrossmember()
    bottomCrossmember.slots.back = backUpright

    // Front stabilizer with band pegs
    const frontStabilizer = this.createFrontStabilizer(left)
    
    // Top crossmember (diagonal for RM, straight for RML)
    const topCrossmember = this.createTopCrossmember()

    // Connect to front upright
    frontUpright.slots['crossmember-bottom'] = [frontStabilizer, bottomCrossmember]
    frontUpright.slots['crossmember-top'] = topCrossmember
  }

  protected createBottomCrossmember(): PartDefinition {
    return {
      mesh: this.line === 'RM'
        ? getRackModel(this.crossmemberLine, 'crossmember bottom 2')
        : getRackModel(this.crossmemberLine, `crossmember ${this.config.depth}`),
      slots     : {},
      anchor    : 'front',
      materials : this.material,
    }
  }

  protected createFrontStabilizer(left: boolean): PartDefinition {
    const bandPegs = this.createBandPegs(left)
    
    return {
      mesh  : getRackModel(this.crossmemberLine, 'front stabilizer'),
      slots : {
        front: bandPegs,
      },
      materials: this.material,
    }
  }

  protected createBandPegs(left: boolean): PartDefinition[] {
    const bandPegMaterials: MaterialInfo = this.line === 'RM' 
      ? { main: { material: 'isonite' } }
      : {}

    return [
      {
        mesh        : getRackModel(this.storageLine, 'band_peg'),
        slots       : {},
        anchor      : 'front',
        adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }],
        materials   : bandPegMaterials,
      },
      {
        mesh        : getRackModel(this.storageLine, 'band_peg'),
        slots       : {},
        anchor      : 'front',
        adjustments : left ? [] : [{ type: 'rotate', axis: 'Z', amount: 180 }],
        offset      : { y: 0, x: 0, z: -8 },
        materials   : bandPegMaterials,
      },
    ]
  }

  protected createTopCrossmember(): PartDefinition {
    return {
      mesh: this.line === 'RM'
        ? getRackModel(this.crossmemberLine, 'diagonal crossmember')
        : getRackModel(this.crossmemberLine, `crossmember ${this.config.depth}`),
      slots     : {},
      anchor    : 'front',
      materials : this.material,
      offset    : this.line === 'RM' ? { z: -0.2 } : {},
    }
  }

  //  MARK: Jcups
  protected attachJCup(backUpright: PartDefinition, left: boolean): void {
    const jcup = this.accessoryBuilder.createJCup(left)
    backUpright.slots['jcup-high'] = jcup
  }

  protected attachSpotterArms(frontUpright: PartDefinition, left: boolean): void {
    if (!this.config.spotterArms) return

    const spotterArms = this.createSpotterArms(left)
    frontUpright.slots['safety-low'] = spotterArms
  }

  protected createSpotterArms(left: boolean): PartDefinition {
    if (this.line === 'RML') {
      return {
        mesh        : getRackModel('RML_SAFETY_SPOTTER_ARMS', 'safety spotter arms'),
        slots       : {},
        adjustments : left 
          ? [] 
          : [{ axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] }],
        offset: { y: 3 },
      }
    }

    // RM has branding plates
    const brandingPlateMesh = left
      ? `${MONSTER_SPOTTER_ARMS}#left branding plate`
      : `${MONSTER_SPOTTER_ARMS}#right branding plate`

    return {
      mesh  : getRackModel('RM_SAFETY_SPOTTER_ARMS', 'spotter arms'),
      slots : {
        middle: {
          mesh   : brandingPlateMesh,
          slots  : {},
          anchor : 'middle',
        },
      },
      adjustments: left 
        ? [] 
        : [{ axis: 'Z', type: 'rotate', amount: 180, pieces: ['clasp'] }],
      offset: { y: 3 },
    }
  }

  protected attachPullupBar(frontUpright: PartDefinition, left: boolean): void {
    if (!left || !this.config.front) return

    // Special case: beam with gusset
    if (this.config.front === 'beam') {
      frontUpright.slots.top = this.createBeamWithGusset()
      
      return
    }

    // Socket pullup bars
    if (this.config.front.toLowerCase().startsWith('socket')) {
      frontUpright.slots.top = this.createSocketPullupBar()
      
      return
    }

    // Standard pullup bars
    frontUpright.slots.top = {
      mesh      : getRackModel(this.pullupLine, this.config.front),
      slots     : {},
      anchor    : 'top',
      materials : this.material,
    }

    // Crown beam gets black material
    if (this.config.front === 'crown') {
      this.material.beam = { name: 'metal', color: 'black' }
    }
  }

  // MARK: Socket Pullup
  protected createSocketPullupBar(): PartDefinition {
    const [socket, type = 'bar', color = 'matte', knurl] = this.config.front!.split(' ')

    const barMaterials: MaterialValue[] = [
      { name: 'bar', material: color as MaterialValue['material'] },
    ]

    // Add knurl material
    if (knurl === 'knurl') {
      barMaterials.push({ 
        name     : 'knurl', 
        material : 'knurl', 
        color    : color === 'matte' ? '#666666' : '#cccccc', 
      })
    } else {
      barMaterials.push({ name: 'knurl', material: color as MaterialValue['material'] })
    }

    // Hide sticker on stainless steel bars
    let roguePullupSticker = null
    if (color === 'stainless') {
      roguePullupSticker = { color: '#cccccc', material: 'stainless', config: { opacity: 0 } }
    }

    const pullupMaterials: MaterialInfo = this.config.materials?.pullup as MaterialInfo
      || { bar: barMaterials, logo: roguePullupSticker }

    return {
      mesh  : getRackModel(this.pullupLine, 'socket'),
      slots : {
        bar: {
          mesh      : getRackModel(this.pullupLine, `socket ${type}`),
          slots     : {},
          effects   : this.config.ghostRack ? { opacity: 0.2 } : {},
          materials : pullupMaterials,
        },
      },
      anchor    : 'top',
      materials : this.material,
    }
  }

  protected createBeamWithGusset(): PartDefinition {
    return {
      mesh        : getRackModel('RM_CROSSMEMBERS', 'crossmember top 4'),
      slots       : {},
      anchor      : 'snowman_bottom',
      materials   : this.material,
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
    }
  }

  protected attachNameplate(backUpright: PartDefinition, left: boolean): void {
    if (!left) return

    const nameplateMaterials: MaterialInfo = this.line === 'RM'
      ? { 
        main     : { name: 'metal', material: 'mg', color: 'red' }, 
        hardware : { material: this.config.hardware }, 
      }
      : this.material

    backUpright.slots.top = {
      mesh      : getRackModel(`${this.line}_NAMEPLATE`, 'nameplate'),
      slots     : {},
      anchor    : this.line === 'RM' ? 'left' : '',
      materials : nameplateMaterials,
    }
  }

  protected attachPlateStorage(backUpright: PartDefinition, left: boolean): void {
    if (!this.config.plateStorage) return

    const storagePinMaterials: MaterialInfo = { hardware: { material: 'isonite' } }

    const storagePins = addPlateStorage(
      this.config,
      left,
      storagePinMaterials,
      this.sideTransform,
      this.config.plateStorage
    )

    backUpright.slots.storage = storagePins
  }

  // MARK: Double Rack
  protected addDoubleRackComponents(backUpright: PartDefinition, left: boolean): void {
    // Create mirrored back structure
    const doubleRackBackUpright = this.createDoubleRackBackUpright()
    const doubleRackBottomCrossmember = this.createDoubleRackBottomCrossmember()
    const doubleRackFrontStabilizer = this.createDoubleRackFrontStabilizer()
    const backDiagonalCrossmember = this.createBackDiagonalCrossmember()

    // Connect structure
    doubleRackBottomCrossmember.slots.back = doubleRackBackUpright
    doubleRackBackUpright.slots['crossmember-bottom'] = doubleRackFrontStabilizer
    doubleRackFrontStabilizer.slots.front = this.createDoubleRackBandPegs(left)

    // Add accessories to double rack back upright
    this.attachDoubleRackJCup(doubleRackBackUpright, left)
    this.attachDoubleRackSpotterArms(doubleRackBackUpright, left)
    this.attachDoubleRackPullupBar(doubleRackBackUpright, left)

    // Connect to original back upright
    backUpright.slots['crossmember-top'] = backDiagonalCrossmember
    backUpright.slots['crossmember-bottom'] = doubleRackBottomCrossmember
  }

  protected createDoubleRackBackUpright(): PartDefinition {
    return {
      mesh      : getRackModel(this.uprightLine, 'upright numbered 100'),
      slots     : {},
      anchor    : 'crossmember-bottom',
      materials : this.material,
      ...this.sideTransform,
    }
  }

  protected createDoubleRackBottomCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel(this.crossmemberLine, 'crossmember bottom 2'),
      slots     : {},
      anchor    : 'front',
      materials : this.material,
    }
  }

  protected createDoubleRackFrontStabilizer(): PartDefinition {
    return {
      mesh        : getRackModel(this.crossmemberLine, 'front stabilizer'),
      slots       : {},
      anchor      : 'front',
      materials   : this.material,
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }
  }

  protected createBackDiagonalCrossmember(): PartDefinition {
    return {
      mesh        : getRackModel('RM_CROSSMEMBERS', 'diagonal crossmember'),
      slots       : {},
      anchor      : 'front',
      materials   : this.material,
      adjustments : [{ type: 'rotate', amount: 180, axis: 'Y' }],
      offset      : { y: 10, z: -26.8 },
    }
  }

  protected createDoubleRackBandPegs(left: boolean): PartDefinition[] {
    const bandPegMaterials: MaterialInfo = this.line === 'RM' 
      ? { main: { material: 'isonite' } }
      : {}

    const baseAdjustments = left
      ? [{ type: 'flip', axis: 'X' }]
      : [{ type: 'rotate', axis: 'Z', amount: 180 }, { type: 'flip', axis: 'X' }]

    return [
      {
        mesh        : getRackModel(this.storageLine, 'band_peg'),
        slots       : {},
        anchor      : 'front',
        materials   : bandPegMaterials,
        adjustments : baseAdjustments as Adjustment[],
      },
      {
        mesh        : getRackModel(this.storageLine, 'band_peg'),
        slots       : {},
        anchor      : 'front',
        materials   : bandPegMaterials,
        adjustments : baseAdjustments as Adjustment[],
        offset      : { y: 0, x: 0, z: -8 },
      },
    ]
  }

  protected attachDoubleRackJCup(doubleRackBackUpright: PartDefinition, left: boolean): void {
    const ghostJcupMaterials = this.getDoubleRackGhostJcupMaterials(left)
    
    const adjustments: Adjustment[] = left
      ? [{ type: 'flip', axis: 'Z' }]
      : [{ type: 'flip', axis: 'Z' }, { type: 'flip', axis: 'X' }]

    const jcup: PartDefinition = {
      mesh      : getRackModel(this.jcupLine, this.config.jcup!),
      slots     : {},
      materials : this.config.jcup === 'ghost-jcup' ? ghostJcupMaterials : {},
      adjustments,
    }

    doubleRackBackUpright.slots['jcup-high'] = jcup
  }

  protected getDoubleRackGhostJcupMaterials(left: boolean): MaterialInfo {
    const leftGhostJcupSticker = { 
      sticker: { 
        color  : '#ffffff', 
        config : { 
          maps: { 
            map: 'https://assets.roguefitness.com/w_512,q_auto,f_auto/glb/txt/jcups/ghost_right.jpg', 
          }, 
        }, 
      }, 
    }

    if (left) {
      return leftGhostJcupSticker
    }

    if (this.line === 'RM' && !left) {
      return { pin: { material: 'stainless' } }
    }

    if (this.line === 'RM' && left) {
      return { pin: { material: 'stainless' }, ...leftGhostJcupSticker }
    }

    return {}
  }

  protected attachDoubleRackSpotterArms(doubleRackBackUpright: PartDefinition, left: boolean): void {
    if (!this.config.spotterArms || this.config.spotterArms <= 1) return

    // Branding plates are reversed for double rack
    const brandingPlateMesh = left
      ? `${MONSTER_SPOTTER_ARMS}#right branding plate`
      : `${MONSTER_SPOTTER_ARMS}#left branding plate`

    const spotterArms: PartDefinition = {
      mesh  : getRackModel('RM_SAFETY_SPOTTER_ARMS', 'spotter arms'),
      slots : {
        middle: {
          mesh   : brandingPlateMesh,
          slots  : {},
          anchor : 'middle',
        },
      },
      offset      : { y: 3 },
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
    }

    doubleRackBackUpright.slots['safety-low'] = spotterArms
  }

  protected attachDoubleRackPullupBar(doubleRackBackUpright: PartDefinition, left: boolean): void {
    if (!left || !this.config.front) return

    // Special case: beam with gusset (reversed)
    if (this.config.front === 'beam') {
      doubleRackBackUpright.slots.top = this.createDoubleRackBeamWithGusset()
      
      return
    }

    // Socket pullup bars (reversed)
    if (this.config.front.toLowerCase().startsWith('socket')) {
      doubleRackBackUpright.slots.top = this.createDoubleRackSocketPullupBar()
      
      return
    }

    // Standard pullup bars (reversed)
    doubleRackBackUpright.slots.top = {
      mesh        : getRackModel(this.pullupLine, this.config.front),
      slots       : {},
      anchor      : 'top',
      materials   : this.material,
      adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      offset      : { x: 46 },
    }
  }

  protected createDoubleRackSocketPullupBar(): PartDefinition {
    const [socket, type = 'bar', color = 'matte', knurl] = this.config.front!.split(' ')

    const barMaterials: MaterialValue[] = [
      { name: 'bar', material: color as MaterialValue['material'] },
    ]

    if (knurl === 'knurl') {
      barMaterials.push({ 
        name     : 'knurl', 
        material : 'knurl', 
        color    : color === 'matte' ? '#666666' : '#cccccc', 
      })
    } else {
      barMaterials.push({ name: 'knurl', material: color as MaterialValue['material'] })
    }

    let roguePullupSticker = null
    if (color === 'stainless') {
      roguePullupSticker = { color: '#cccccc', material: 'stainless', config: { opacity: 0 } }
    }

    const pullupMaterials: MaterialInfo = this.config.materials?.pullup as MaterialInfo
      || { bar: barMaterials, logo: roguePullupSticker }

    return {
      mesh  : getRackModel(this.pullupLine, 'socket'),
      slots : {
        bar: {
          mesh        : getRackModel(this.pullupLine, `socket ${type}`),
          slots       : {},
          effects     : this.config.ghostRack ? { opacity: 0.2 } : {},
          materials   : pullupMaterials,
          adjustments : [{ type: 'rotate', axis: 'Y', amount: 180 }],
          offset      : { x: 41 },
        },
      },
      anchor    : 'top',
      materials : this.material,
    }
  }
  
  protected createDoubleRackBeamWithGusset(): PartDefinition {
    return {
      mesh        : getRackModel('RM_CROSSMEMBERS', 'crossmember top 4'),
      slots       : {},
      anchor      : 'front',
      materials   : this.material,
      adjustments : [
        { type: 'rotate', axis: 'Y', amount: 90 },
        { type: 'flip', axis: 'Y' },
        { type: 'rotate', axis: 'X', amount: 180 },
        { type: 'flip', axis: 'X' },
      ],
      offset: { y: 1 },
    }
  }
}